import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Trash } from 'lucide-react';
import { ulid } from '../lib/ulid.js';
import { Input } from '../ui/Input.js';
import { Button } from '../ui/Button.js';
import { Modal } from '../ui/Modal.js';
import { Dialogue } from '../ui/Dialogue.js';
import { Field } from '../ui/Field.js';
import { DateField } from '../ui/DateField.js';
import { useTasks } from '../stores/tasks.js';
import { useProjects } from '../stores/projects.js';
import { useUi } from '../stores/ui.js';
import { listTasks, type Subtask, type Task } from '../api/tasks.js';
import { cn } from '../lib/cn.js';
import { parseQuickAdd } from '../lib/parser/index.js';
import { todayInParis } from '../lib/dates.js';
import { HighlightedInput } from './HighlightedInput.js';
import { ApercuLie, aDesLiens } from './TexteLie.js';
import { ConfirmDialog } from '../ui/ConfirmDialog.js';
import { errorMessage } from '../lib/errors.js';

const RRULE_PRESETS: Array<{ label: string; value: string | null }> = [
  { label: 'None', value: null },
  { label: 'Daily', value: 'FREQ=DAILY' },
  { label: 'Weekly', value: 'FREQ=WEEKLY' },
  { label: 'Monthly', value: 'FREQ=MONTHLY' },
];

/** La borne de `updateTaskSchema` (backend/src/schemas/tasks.ts) : au-delà, le PATCH part en 400. */
const DESCRIPTION_MAX = 20000;

export function TaskDetailModal(): React.ReactElement | null {
  const taskId = useUi((s) => s.taskDetailId);
  const close = useUi((s) => s.closeTaskDetail);
  const tasks = useTasks((s) => s.tasks);
  const projects = useProjects((s) => s.projects);
  const { update, remove } = useTasks();

  const storeTask = useMemo(
    () => (taskId ? tasks.find((t) => t.taskId === taskId) : undefined),
    [taskId, tasks]
  );
  const [fetchedTask, setFetchedTask] = useState<Task | null>(null);

  useEffect(() => {
    if (!taskId || storeTask) { setFetchedTask(null); return; }
    listTasks('all').then((all) => {
      setFetchedTask(all.find((t) => t.taskId === taskId) ?? null);
    }).catch(() => setFetchedTask(null));
  }, [taskId, storeTask]);

  const task = storeTask ?? fetchedTask ?? undefined;

  const [draft, setDraft] = useState<Task | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subtaskInput, setSubtaskInput] = useState('');
  const [confirmingClose, setConfirmingClose] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  // La hauteur du textarea au blur : l'aperçu qui le remplace la garde. Un lien markdown long
  // s'y lit en libellé, plus court que son URL écrite : sans ce plancher, le formulaire remonte
  // sous le pointeur entre le mousedown (qui fait le blur) et le mouseup, et le clic sur Save
  // est perdu.
  const [descriptionMinHeight, setDescriptionMinHeight] = useState<number | undefined>();
  const [editingTitle, setEditingTitle] = useState(false);
  const titleRef = useRef<HTMLInputElement>(null);
  const saveRef = useRef<(() => Promise<void>) | undefined>(undefined);
  const closeRef = useRef<(() => void) | undefined>(undefined);

  useEffect(() => {
    if (task) {
      setDraft({ ...task, subtasks: task.subtasks.map((s) => ({ ...s })) });
      setSubtaskInput('');
      setTimeout(() => titleRef.current?.focus(), 0);
    } else {
      setDraft(null);
    }
  }, [task]);

  const pendingSubtask = subtaskInput.trim();
  const dirty =
    task && draft
      ? pendingSubtask !== '' || JSON.stringify(draft) !== JSON.stringify(task)
      : false;

  // La hauteur d'une sous-tâche suit son texte. Mesurée ici, après chaque rendu, et non dans un
  // `ref` : les effets d'un enfant passent avant ceux du parent, donc le <dialog> est déjà
  // ouvert (`showModal()`, ui/Dialogue.tsx) — dans un `ref`, il est encore en `display: none`
  // au premier rendu, et `scrollHeight` vaut 0.
  const subtasksRef = useRef<HTMLDivElement>(null);
  // La description aussi, à la hauteur de son texte ; son aperçu en garde la hauteur au blur
  // (`descriptionMinHeight` : le blur part au mousedown, avant le clic qui l'a causé).
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const editDescriptionRef = useRef<HTMLButtonElement>(null);
  const editTitleRef = useRef<HTMLButtonElement>(null);
  // Le crayon à refocaliser après le rendu, quand on sort d'une édition par Escape.
  const refocusAfterEscape = useRef<React.RefObject<HTMLButtonElement | null> | null>(null);
  useLayoutEffect(() => {
    for (const el of subtasksRef.current?.querySelectorAll<HTMLTextAreaElement>(
      'textarea[data-auto-hauteur]'
    ) ?? []) {
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    }
    const d = descriptionRef.current;
    if (d) {
      d.style.height = 'auto';
      if (d.value) d.style.height = `${d.scrollHeight + d.offsetHeight - d.clientHeight}px`;
    }
    // Après Escape, le focus revient au crayon plutôt que de tomber sur <body>.
    if (refocusAfterEscape.current) {
      refocusAfterEscape.current.current?.focus();
      refocusAfterEscape.current = null;
    }
  });

  // Une confirmation ne survit pas à la tâche qu'elle concernait.
  useEffect(() => {
    setConfirmingClose(false);
    setConfirmingDelete(false);
    setEditingDescription(false);
    setDescriptionMinHeight(undefined);
    setEditingTitle(false);
    setError(null);
  }, [taskId]);

  useEffect(() => {
    if (!taskId) return;
    const onKey = (e: KeyboardEvent): void => {
      // Sous une confirmation, le raccourci n'appartient plus à la tâche : il la fermerait
      // derrière « Delete ? » en laissant la confirmation armée pour la tâche suivante.
      if (confirmingClose || confirmingDelete) return;
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        if (dirty) void saveRef.current?.();
        else closeRef.current?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [taskId, dirty, confirmingClose, confirmingDelete]);

  if (!taskId || !draft) return null;

  const descriptionLength = draft.description?.length ?? 0;
  const descriptionTooLong = descriptionLength > DESCRIPTION_MAX;
  // En lecture, la description est rendue avec ses liens cliquables ; le textarea ne sert qu'à
  // l'éditer (clic sur le texte ou crayon, retour au rendu au blur ou à Escape). Vide, il n'y a
  // rien à lire : le textarea reste.
  const readingDescription = !editingDescription && !!draft.description;
  // Le titre aussi, mais seulement s'il porte un lien : sans lien, rien à cliquer, et la saisie
  // garde le focus à l'ouverture comme avant. Avec un lien, `titleRef` est vide à l'ouverture :
  // le focus initial va où `showModal()` le pose.
  const readingTitle = !editingTitle && aDesLiens(draft.title);

  function patch<K extends keyof Task>(key: K, value: Task[K]): void {
    setDraft((d) => (d ? { ...d, [key]: value } : d));
  }

  function onTitleChange(val: string): void {
    const parsed = parseQuickAdd(val, todayInParis());
    const norm = (s: string): string => s.toLowerCase().replace(/[-_\s]+/g, '');
    setDraft((d) => {
      if (!d) return d;
      const matchedProject =
        parsed.projectName &&
        projects.find((p) => norm(p.name) === norm(parsed.projectName!));
      return {
        ...d,
        title: val,
        ...(parsed.dueDate !== null ? { dueDate: parsed.dueDate } : {}),
        ...(parsed.rrule !== null ? { rrule: parsed.rrule } : {}),
        ...(matchedProject ? { projectId: matchedProject.projectId } : {}),
      };
    });
  }

  function updateSubtask(id: string, partial: Partial<Subtask>): void {
    setDraft((d) =>
      d
        ? {
            ...d,
            subtasks: d.subtasks.map((s) =>
              s.id === id ? { ...s, ...partial } : s
            ),
          }
        : d
    );
  }

  function addSubtask(): void {
    if (!subtaskInput.trim()) return;
    setDraft((d) =>
      d
        ? {
            ...d,
            subtasks: [
              ...d.subtasks,
              {
                id: ulid(),
                title: subtaskInput.trim(),
                done: false,
                order: d.subtasks.length,
              },
            ],
          }
        : d
    );
    setSubtaskInput('');
  }

  function removeSubtask(id: string): void {
    setDraft((d) =>
      d ? { ...d, subtasks: d.subtasks.filter((s) => s.id !== id) } : d
    );
  }

  async function save(): Promise<void> {
    if (!draft || !dirty) {
      close();
      return;
    }
    if (descriptionTooLong) {
      setError(`Description too long (max ${DESCRIPTION_MAX} chars).`);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const parsed = parseQuickAdd(draft.title, todayInParis());
      const cleanTitle = parsed.title || draft.title.trim();
      const subtasks = pendingSubtask
        ? [
            ...draft.subtasks,
            {
              id: ulid(),
              title: pendingSubtask,
              done: false,
              order: draft.subtasks.length,
            },
          ]
        : draft.subtasks;
      await update(draft.taskId, {
        title: cleanTitle,
        description: draft.description,
        projectId: draft.projectId,
        dueDate: draft.dueDate,
        rrule: draft.rrule,
        subtasks,
      });
      close();
    } catch (err) {
      // Sans ce `catch`, un 400 laissait le dialogue ouvert sans un mot (2026-10-02).
      setError(errorMessage(err, 'Save failed.'));
    } finally {
      setSaving(false);
    }
  }

  saveRef.current = save;
  closeRef.current = close;

  async function confirmDelete(): Promise<void> {
    if (!draft) return;
    setConfirmingDelete(false);
    await remove(draft.taskId);
    close();
  }

  const requestClose = (): void => {
    if (dirty) setConfirmingClose(true);
    else close();
  };

  return (
    <Modal
      open
      onClose={requestClose}
      title="Task"
      size="lg"
      initialFocusRef={titleRef}
      footer={
        <>
          <Button variant="danger" className="mr-auto" onClick={() => setConfirmingDelete(true)}>
            Delete
          </Button>
          <Button variant="ghost" onClick={requestClose}>
            Cancel
          </Button>
          <Button
            onClick={save}
            loading={saving}
            // Le mousedown ne prend pas le focus : la saisie en cours ne fait pas son blur, rien
            // ne bascule en aperçu sous le pointeur, le clic arrive sur Save. Le plancher de
            // `descriptionMinHeight` ne couvre que le cas où l'aperçu est plus court.
            onMouseDown={(e) => e.preventDefault()}
          >
            {dirty ? 'Save' : 'Close'}
          </Button>
        </>
      }
    >
      <div className="pile">
        <Field label="Title" group>
          {readingTitle ? (
            <ApercuLie
              ref={editTitleRef}
              text={draft.title}
              onEdit={() => setEditingTitle(true)}
              editLabel="Edit title"
              // Sur une ligne, comme la saisie : le passage édition → lecture ne décale pas le
              // formulaire sous le pointeur (le blur part au mousedown).
              className="truncate"
            />
          ) : (
            // Le menu des projets vit dans la saisie : un focus qui y passe n'est pas un blur.
            // Escape ferme d'abord ce menu (`defaultPrevented`), puis rend le rendu.
            <div
              onFocus={() => setEditingTitle(true)}
              onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) setEditingTitle(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape' && !e.defaultPrevented && aDesLiens(draft.title)) {
                  e.preventDefault();
                  refocusAfterEscape.current = editTitleRef;
                  setEditingTitle(false);
                }
              }}
            >
              <HighlightedInput
                ref={titleRef}
                autoFocus={editingTitle}
                value={draft.title}
                onChange={onTitleChange}
                today={todayInParis()}
                placeholder="Title — type #project, tomorrow, every 23/01…"
                aria-label="Title"
              />
            </div>
          )}
        </Field>

        <Field
          label="Description"
          group
          hint={
            descriptionLength > DESCRIPTION_MAX * 0.9 ? (
              <span className={cn(descriptionTooLong && 'text-error')}>
                {descriptionLength} / {DESCRIPTION_MAX}
              </span>
            ) : undefined
          }
        >
          {readingDescription ? (
            <ApercuLie
              ref={editDescriptionRef}
              text={draft.description ?? ''}
              onEdit={() => setEditingDescription(true)}
              editLabel="Edit description"
              className="whitespace-pre-wrap min-h-24"
              minHeight={descriptionMinHeight}
            />
          ) : (
            <textarea
              ref={descriptionRef}
              aria-label="Description"
              value={draft.description ?? ''}
              onChange={(e) => patch('description', e.target.value || null)}
              // Sans `onFocus`, une description vide (textarea affiché d'office) basculait en
              // rendu dès la première lettre.
              onFocus={() => setEditingDescription(true)}
              onBlur={(e) => {
                setDescriptionMinHeight(e.currentTarget.offsetHeight);
                setEditingDescription(false);
              }}
              onKeyDown={(e) => {
                // Escape rend le rendu sans fermer la tâche : une touche empêchée n'émet pas
                // `cancel` sur le <dialog> (ui/Dialogue.tsx).
                if (e.key === 'Escape' && draft.description) {
                  e.preventDefault();
                  refocusAfterEscape.current = editDescriptionRef;
                  setDescriptionMinHeight(e.currentTarget.offsetHeight);
                  setEditingDescription(false);
                }
              }}
              autoFocus={editingDescription}
              rows={2}
              className="saisie"
              placeholder="Notes, context, details…"
            />
          )}
        </Field>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Project">
            <select
              value={draft.projectId}
              onChange={(e) => patch('projectId', e.target.value)}
              className="saisie"
            >
              {projects.map((p) => (
                <option key={p.projectId} value={p.projectId}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Due date" group>
            <DateField
              value={draft.dueDate}
              onChange={(d) => patch('dueDate', d)}
              label="Pick due date"
            />
          </Field>
        </div>

        <Field label="Repeat" group>
          <div className="puces">
            {RRULE_PRESETS.map((p) => {
              const active =
                (draft.rrule ?? null) === p.value || (p.value === null && !draft.rrule);
              return (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => patch('rrule', p.value)}
                  aria-pressed={active}
                  className="puce"
                >
                  {p.label}
                </button>
              );
            })}
          </div>
          <Input
            value={draft.rrule ?? ''}
            onChange={(e) => patch('rrule', e.target.value || null)}
            placeholder="Custom RRULE (e.g. FREQ=WEEKLY;BYDAY=MO,WE,FR)"
            aria-label="Custom RRULE"
            className="font-mono"
          />
        </Field>

        <Field
          label={`Subtasks (${draft.subtasks.filter((s) => s.done).length}/${draft.subtasks.length})`}
          group
        >
          <div className="liste" ref={subtasksRef}>
            {draft.subtasks.map((s) => (
              <div key={s.id} className="ligne px-0 items-start">
                <input
                  type="checkbox"
                  checked={s.done}
                  onChange={(e) => updateSubtask(s.id, { done: e.target.checked })}
                  aria-label={`Done: ${s.title}`}
                  className="coche mt-1.5"
                />
                <textarea
                  value={s.title}
                  onChange={(e) => updateSubtask(s.id, { title: e.target.value })}
                  rows={1}
                  aria-label="Subtask"
                  data-auto-hauteur
                  className={cn(
                    'flex-1 min-w-0 bg-transparent resize-none leading-snug py-1.5',
                    s.done && 'line-through text-muted'
                  )}
                />
                <Button
                  variant="quiet"
                  size="icon-small"
                  onClick={() => removeSubtask(s.id)}
                  className="ligne-action"
                  aria-label={`Remove subtask: ${s.title}`}
                >
                  <Trash size={14} aria-hidden />
                </Button>
              </div>
            ))}
          </div>
          <Input
            value={subtaskInput}
            onChange={(e) => setSubtaskInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addSubtask();
              }
            }}
            placeholder="Add subtask (Enter)"
            aria-label="Add subtask"
          />
        </Field>

        <p className="alerte alerte-erreur" role="alert">
          {error}
        </p>
      </div>

      <ConfirmDialog
        open={confirmingDelete}
        title="Delete this task?"
        message="This can't be undone."
        confirmLabel="Delete"
        onConfirm={() => void confirmDelete()}
        onCancel={() => setConfirmingDelete(false)}
      />

      <Dialogue
        open={confirmingClose}
        onClose={() => setConfirmingClose(false)}
        title="Unsaved changes"
        size="sm"
        hideClose
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmingClose(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                setConfirmingClose(false);
                close();
              }}
            >
              Discard
            </Button>
            <Button
              loading={saving}
              onClick={() => {
                setConfirmingClose(false);
                void save();
              }}
            >
              Save
            </Button>
          </>
        }
      >
        <p className="discret">You have unsaved changes. Save them?</p>
      </Dialogue>
    </Modal>
  );
}
